/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';

import SlideIn from '../../../Common/Components/SlideIn';
import Textbox from '../../../Common/Components/Textbox';
import Modal from '../../../Common/Components/Modal';
import Button from '../../../Common/Components/Button';
import SlideinListElementBig from '../Components/SlideinListElementBig';

import { ReactComponent as PinIcon } from '../../../img/icons/pin.svg';
import { NodeObject } from '../../../types/NodeObject';
import { SlideInPanelPropsType } from '../../../types/SlideInPanelPropsType';
import Utils from '../../../utils/Utils';
import { putRequest } from '../../../utils/fetch';
import { useAppContext } from '../../../utils/AppContext';
import { GpsObject } from '../../../types/GpsObject';

function LightsLocation(props: SlideInPanelPropsType): JSX.Element {
  const { selectedCustomer, selectedSite, selectedItems, updateNodes, gpsPinPosition, setGpsPinPosition = () => undefined } = props;
  const selectedNode = selectedItems.values().next().value as NodeObject;

  const { addNotification } = useAppContext();
  const [latitude, setLatitude] = useState(gpsPinPosition?.lat || selectedNode.latitudeUser);
  const [longitude, setLongitude] = useState(gpsPinPosition?.lng || selectedNode.longitudeUser);
  const [openModal, setOpenModal] = useState(false);
  const { data: gpsResp } = useSWR<GpsObject | undefined>([
    `/organizations/${selectedCustomer.id}/sites/${selectedSite.id}/nodes/${selectedNode.nodeid}/gps`,
    `GetGps-${selectedNode.nodeid}`,
  ]);

  useEffect(() => {
    if (gpsPinPosition && gpsPinPosition.lat && gpsPinPosition.lng && gpsPinPosition.lat !== latitude && gpsPinPosition.lng !== longitude) {
      setLatitude(gpsPinPosition.lat);
      setLongitude(gpsPinPosition.lng);
      setGpsPinPosition(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsPinPosition]);

  const sampleSize = gpsResp?.sampleSize ? gpsResp.sampleSize.toString() : (selectedNode.gpsSampleSize ? selectedNode.gpsSampleSize.toString() : '--');
  const accuracy = gpsResp?.accuracy ? `${gpsResp.accuracy.toString()} m` : (selectedNode.gpsAccuracy ? `${selectedNode.gpsAccuracy} m` : '--');

  return (
    <div className="location">
      <SlideIn>
        <>
          <div className="slide-in__title">
            <span>Location</span>
            <div className="slide-in__title-subtitle">
              <span>Node ID</span>
              {selectedNode.nodeid}
            </div>
          </div>
          <div className="slide-in__content">
            <div className="slide-in__content-title">
              <p>GPS Location</p>
              <p className="slide-in__content-subtitle">
                <span>Updated</span>
                {Utils.getConvertedDate(gpsResp?.updatedAt) === '' ? '--' : Utils.getConvertedDate(gpsResp?.updatedAt)}
              </p>
            </div>
            <SlideinListElementBig
              title="User defined Latitude"
              value={selectedNode.latitudeUser}
              valueType="small"
            />
            <SlideinListElementBig
              title="User defined Longitude"
              value={selectedNode.longitudeUser}
              valueType="small"
            />
            <SlideinListElementBig
              title="GPS Latitude"
              value={selectedNode.latitudeGps}
              valueType="small"
            />
            <SlideinListElementBig
              title="GPS Longitude"
              value={selectedNode.longitudeGps}
              valueType="small"
            />
            <SlideinListElementBig
              title="Sample count"
              value={sampleSize}
              valueType="small"
            />
            <SlideinListElementBig
              title="Precision metric"
              value={accuracy}
              border="light"
              valueType="small"
            />
          </div>
        </>
      </SlideIn>
      <SlideIn isExpanded>
        <>
          <div className="slide-in__content location__content">
            <div className="slide-in__title" />
            <div className="location-title__container">
              <p className="location-title__container-title">
                Display location
              </p>
              <p className="location-title__container-subtitle">
                <span>Adjust display location</span>
              </p>
              <div className="location__content-icon-container">
                <span>
                  <PinIcon onClick={() => setGpsPinPosition((oldVal) => {
                    if (oldVal && oldVal.lat && oldVal.lng) {
                      return undefined;
                    }
                    return { lat: latitude, lng: longitude };
                  })}
                  />
                </span>
                <p>
                  Click on the pin, then click on the map to set the location
                  or enter latitude and longitude values below.
                </p>
              </div>
            </div>
            <form action="">
              <Textbox
                label="User defined Latitude"
                value={latitude}
                onChange={(event) => setLatitude(event.target.value)}
                textboxType="long"
                styleType="dark"
              />
              <Textbox
                label="User defined Longitude"
                value={longitude}
                onChange={(event) => setLongitude(event.target.value)}
                textboxType="long"
                styleType="dark"
              />
            </form>
            {Utils.isNonReadOnly() && (
            <div className="slide-in__link multiple">
              <Button
                onClick={() => {
                  setLatitude(selectedNode.latitudeGps);
                  setLongitude(selectedNode.longitudeGps);
                }}
                label="Reset to GPS"
                extraClasses="margin-right-10"
              />
              <Button onClick={() => setOpenModal(true)} label="Update" />
            </div>
            )}
          </div>
        </>
      </SlideIn>
      {
        openModal && (
          <Modal
            width="325"
            setModalOpen={setOpenModal}
            title="Update display location?"
            subtitle="Show this node at new location?"
            secondaryButtonLabel="Cancel"
            primaryButtonLabel="Submit"
            primaryButtonAction={async () => {
              try {
                const putBody = {
                  latitudeUserDefined: parseFloat(latitude),
                  longitudeUserDefined: parseFloat(longitude),
                };

                const result = await putRequest(
                  `/organizations/${selectedCustomer.id}/sites/${selectedSite.id}/nodes/${selectedNode.nodeid}/gps`,
                  putBody,
                );

                if (!result.error) {
                  addNotification({ type: 'info', message: 'Your "Update display location" operation is completed.' });
                  setOpenModal(false);

                  if (updateNodes) {
                    setTimeout(() => updateNodes(), 1000);
                  }
                } else {
                  addNotification({ type: 'error', message: `Your "Update display location" operation has failed: ${result.error}` });
                }
              } catch (e) {
                addNotification({ type: 'error', message: 'Your "Update display location" operation has failed.' });
              }
            }}
          />
        )
      }
    </div>
  );
}

export default LightsLocation;
